import { formatUnits } from 'viem';

const RAY = 1e27;
const SECONDS_PER_YEAR = 31_536_000;

export function toNumber(raw: bigint | string, decimals: number): number {
  return Number(formatUnits(typeof raw === 'string' ? BigInt(raw) : raw, decimals));
}

/** USD value (float) of a raw token amount at an 8-decimal oracle price. */
export function usdValue(raw: bigint, decimals: number, price8: bigint): number {
  return Number(formatUnits(raw * price8, decimals + 8));
}

export function fmtUsd(v: number, opts: { compact?: boolean; digits?: number } = {}): string {
  if (!Number.isFinite(v)) return '–';
  if (opts.compact && Math.abs(v) >= 100_000) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  }
  const digits = opts.digits ?? (Math.abs(v) > 0 && Math.abs(v) < 1 ? 4 : 2);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits }).format(v);
}

export function fmtAmount(v: number, maxDigits = 4): string {
  if (!Number.isFinite(v)) return '–';
  if (v !== 0 && Math.abs(v) < 10 ** -maxDigits) return `<${(10 ** -maxDigits).toFixed(maxDigits)}`;
  if (Math.abs(v) >= 1_000_000) return new Intl.NumberFormat('en-US', { notation: 'compact', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: Math.abs(v) >= 1000 ? 2 : maxDigits }).format(v);
}

export function fmtToken(raw: bigint | string, decimals: number, maxDigits = 4): string {
  return fmtAmount(toNumber(raw, decimals), maxDigits);
}

export function fmtPct(fraction: number, digits = 2): string {
  if (!Number.isFinite(fraction)) return '–';
  const text = (fraction * 100).toFixed(digits);
  return `${Number(text) === 0 ? text.replace('-', '') : text}%`; // never "-0.00%"
}
export const fmtBps = (bps: number, digits = 0): string => `${(bps / 100).toFixed(digits)}%`;

/** Aave publishes per-second-compounded APRs in ray; APY is what a position actually accrues over a year. */
export function rayToApy(rateRay: string | bigint): number {
  const apr = Number(rateRay) / RAY;
  return (1 + apr / SECONDS_PER_YEAR) ** SECONDS_PER_YEAR - 1;
}
export const rayToApr = (rateRay: string | bigint): number => Number(rateRay) / RAY;

export const shortAddr = (a: string): string => `${a.slice(0, 6)}…${a.slice(-4)}`;

export function timeAgo(unixSec: number, now = Date.now()): string {
  const s = Math.max(0, Math.floor(now / 1000) - unixSec);
  for (const [size, unit] of [[86_400, 'd'], [3_600, 'h'], [60, 'm']] as const) if (s >= size) return `${Math.floor(s / size)}${unit} ago`;
  return 'just now';
}

/** Compact token amounts the way lending dashboards show them: 1.00M, 999.92K, 1,000, 1.3719 (in full below 10,000). */
export function fmtCompact(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '–';
  if (Math.abs(v) >= 10_000) return new Intl.NumberFormat('en-US', { notation: 'compact', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  return fmtAmount(v, digits === 2 && Math.abs(v) < 1 ? 4 : digits);
}

/** Parses user input into raw units; null when empty or malformed (never throws while the user is typing). */
export function parseAmount(text: string, decimals: number): bigint | null {
  const t = text.trim().replace(/,/g, '');
  if (!/^\d*\.?\d*$/.test(t) || t === '' || t === '.') return null;
  const [int = '0', frac = ''] = t.split('.');
  return BigInt(int || '0') * 10n ** BigInt(decimals) + BigInt((frac.slice(0, decimals) || '0').padEnd(decimals, '0'));
}

export function rawToInput(raw: bigint, decimals: number): string {
  const s = formatUnits(raw, decimals);
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/** ERC-8056 multiplier (1e18 = 1.0) as a decimal, trailing zeros trimmed, at least one decimal. */
export function fmtMultiplier(m: string | bigint): string {
  const s = BigInt(m).toString().padStart(19, '0');
  const frac = s.slice(-18).replace(/0+$/, '');
  return `${s.slice(0, -18)}.${frac.length ? frac.slice(0, 9) : '0'}`;
}
